<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Edit Medication </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <div class="mb-4">
              <v-text-field
                v-model="formData.medication_name"
                :rules="[(v) => !!v || 'Medication Name is required']"
                label="Medication Name"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
              />

              <v-select
                v-model="formData.medication_category_id"
                :items="categories"
                :item-title="(c) => formatMedicationCategoryName(c.medication_category_name)"
                item-value="medication_category_id"
                :rules="[(v) => !!v || 'Category is required']"
                label="Category"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
                @update:model-value="onCategoryChange"
              />

              <v-select
                v-if="hasMedicationClasses"
                v-model="formData.medication_class_id"
                :items="filteredMedicationClasses"
                item-title="class_name"
                item-value="class_id"
                :rules="[(v) => !!v || 'Medication Class is required']"
                label="Medication Class"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
                @update:model-value="handleMedicationClassChange"
              />

              <v-text-field
                v-model="formData.brand_name"
                label="Brand Name"
                variant="outlined"
                density="compact"
                class="mb-2"
              />
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="handleClose">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!valid || loading"
            :loading="loading"
            @click="handleSubmit"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { edit_master_medications } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import GetMedicationCategories from "@/mixins/Medication/GetMedicationCategories";
import { formatMedicationCategoryName } from "@/utils/medicationCategoryLabel";
import GetMedicationClasses from "@/mixins/Medication/GetMedicationClasses";

export default {
  name: "EditMedicationDialog",
  mixins: [GetMedicationCategories, GetMedicationClasses],
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    medication: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "updated"],
  data() {
    return {
      valid: false,
      loading: false,
      categories: [],
      formData: {
        medication_name: "",
        medication_category_id: null,
        medication_class_id: null,
        medication_class: "",
        brand_name: "",
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    filteredMedicationClasses() {
      if (!this.formData.medication_category_id) {
        return [];
      }
      return this.medicationClassesList.filter(
        (item) =>
          String(item.medication_category_id) ===
          String(this.formData.medication_category_id)
      );
    },
    hasMedicationClasses() {
      return this.filteredMedicationClasses.length > 0;
    },
    selectedMedicationClass() {
      return this.medicationClassesList.find(
        (item) =>
          String(item.class_id) === String(this.formData.medication_class_id)
      );
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadData().then(() => {
          if (this.medication) {
            this.loadMedicationData();
          }
        });
      } else {
        this.resetForm();
      }
    },
    medication: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.modelValue) {
          this.loadMedicationData();
        }
      },
    },
  },
  methods: {
    formatMedicationCategoryName,
    async loadData() {
      try {
        await Promise.all([
          this.fetchMedicationCategories(),
          this.fetchMedicationClasses(),
        ]);
        this.categories = this.medicationCategoriesList.map((item) => ({
          medication_category_id: item.medication_category_id,
          medication_category_name: item.medication_category_name,
        }));
      } catch (error) {
        console.error("Error loading medication data:", error);
      }
    },
    onCategoryChange() {
      this.formData.medication_class_id = null;
      this.formData.medication_class = "";
    },
    handleMedicationClassChange(value) {
      const selectedClass = this.medicationClassesList.find(
        (medClass) => String(medClass.class_id) === String(value)
      );
      if (selectedClass) {
        this.formData.medication_class = selectedClass.class_name;
        if (selectedClass.medication_category_id) {
          this.formData.medication_category_id =
            selectedClass.medication_category_id;
        }
      }
    },
    getSelectedCategoryName() {
      const selectedCategory = this.categories.find(
        (category) =>
          String(category.medication_category_id) ===
          String(this.formData.medication_category_id)
      );
      return (
        selectedCategory?.medication_category_name ||
        this.medication?.medication_category_name ||
        ""
      );
    },
    loadMedicationData() {
      if (!this.medication) {
        return;
      }

      let medicationClassId = this.medication.medication_class_id || "";
      if (!medicationClassId && this.medication.medication_class) {
        const foundClass = this.medicationClassesList.find(
          (medClass) =>
            (medClass.class_name || "").toLowerCase() ===
            (this.medication.medication_class || "").toLowerCase()
        );
        if (foundClass) {
          medicationClassId = foundClass.class_id;
        }
      }

      const matchedClass = this.medicationClassesList.find(
        (medClass) => String(medClass.class_id) === String(medicationClassId)
      );

      let medicationCategoryId =
        this.medication.medication_category_id ||
        matchedClass?.medication_category_id ||
        null;

      if (!medicationCategoryId && this.medication.medication_category_name) {
        const matchedCategory = this.categories.find(
          (category) =>
            (category.medication_category_name || "").toLowerCase() ===
            (this.medication.medication_category_name || "").toLowerCase()
        );
        medicationCategoryId = matchedCategory?.medication_category_id || null;
      }

      this.formData = {
        medication_name: this.medication.medication_name || "",
        medication_category_id: medicationCategoryId,
        medication_class_id: medicationClassId || null,
        medication_class:
          this.medication.medication_class || matchedClass?.class_name || "",
        brand_name: this.medication.brand_name || "",
      };
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        medication_name: "",
        medication_category_id: null,
        medication_class_id: null,
        medication_class: "",
        brand_name: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      if (this.hasMedicationClasses && !this.formData.medication_class_id) {
        return;
      }

      this.loading = true;

      try {
        const selectedClass = this.selectedMedicationClass;
        const input = {
          medication_master_id:
            this.medication.medication_master_id || this.medication.id || "",
          medication_name: this.formData.medication_name.trim(),
          medication_category_id: this.formData.medication_category_id,
          medication_category_name: this.getSelectedCategoryName(),
          medication_class:
            selectedClass?.class_name || this.formData.medication_class || "",
          medication_class_id:
            selectedClass?.class_id || this.formData.medication_class_id || "",
          brand_name: this.formData.brand_name || "",
        };

        const response = await client.graphql({
          query: edit_master_medications,
          variables: {
            input,
          },
        });

        const responseData = JSON.parse(response.data.edit_master_medications);

        if (responseData.status == "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Medication updated successfully",
            color: "success",
          };
          this.$emit("updated", 0);
          this.handleClose();
        } else {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to update medication",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error updating medication:", error);
        this.snackbar = {
          show: true,
          message:
            error.message || "An error occurred while updating the medication",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
